import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Loader2, Search, Mail, Smartphone, AlertCircle, CheckCircle2, Clock, RefreshCw, Reply, MoreVertical, Copy, Archive, RotateCcw, ArchiveRestore, Plus, X, History } from "lucide-react";
import { Input } from "../ui/input";
import { toast } from "sonner";
import axios from "axios";
import { CommunicationDetailModal } from "./CommunicationDetailModal";
import { SmsReplyModal } from "./SmsReplyModal";
import { ComposeMessageModal } from "./ComposeMessageModal";
import { Timeline } from "./Timeline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

const STATUS_STYLE = {
  sent:     { bg: "bg-emerald-50",  text: "text-emerald-700", border: "border-emerald-200", Icon: CheckCircle2 },
  received: { bg: "bg-sky-50",      text: "text-sky-700",     border: "border-sky-200",     Icon: Reply },
  failed:   { bg: "bg-red-50",      text: "text-red-700",     border: "border-red-200",     Icon: AlertCircle },
  failed_permanent: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200",     Icon: AlertCircle },
  pending:  { bg: "bg-amber-50",    text: "text-amber-700",   border: "border-amber-200",   Icon: Clock },
  resolved: { bg: "bg-blue-50",     text: "text-blue-700",    border: "border-blue-200",    Icon: CheckCircle2 },
};

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "sent", label: "Sent" },
  { key: "pending", label: "Pending" },
  { key: "failed", label: "Failed" },
  { key: "resolved", label: "Resolved" },
  { key: "customer_emails", label: "Customer" },
  { key: "admin_emails", label: "Admin" },
  { key: "otp", label: "OTP" },
  { key: "sms", label: "SMS" },
  { key: "inbound", label: "Inbound" },
  { key: "password_reset", label: "Password Reset" },
  { key: "booking", label: "Booking" },
  { key: "trial", label: "Trial" },
  { key: "appointment", label: "Appointment" },
  { key: "payment", label: "Payment" },
];

function formatTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

async function copyToClipboard(text, label) {
  if (!text) { toast.info(`${label} is empty`); return; }
  try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
  catch { toast.error(`Couldn't copy ${label}`); }
}

/**
 * Per-row kebab menu. Uses a local click-outside handler — the parent table row
 * has its own onClick (open detail modal), so every action here calls
 * e.stopPropagation() before doing its thing.
 */
function RowActionsMenu({ row, onArchive, onUnarchive, onDuplicate, onViewTimeline }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const stop = (fn) => (e) => { e.stopPropagation(); setOpen(false); fn?.(); };
  const isArchived = !!row.archived;
  const isSms = !!row.to_phone || !!row.from_phone;
  const isSensitive = !!row.is_sensitive;
  // Section 17 — timeline drill-in is available whenever the comm carries at
  // least one linked ID that the timeline endpoint can scope on.
  const hasLinkedScope = !!(row.customer_id || row.appointment_id || row.trial_lead_id || row.booking_id);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className="p-1 hover:bg-[#FAF8F0] border border-transparent hover:border-[#282828]/12"
        title="Row actions"
        data-testid={`comms-row-menu-${row.id}`}
      >
        <MoreVertical className="w-3.5 h-3.5 text-[#676664]" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-[#282828]/12 shadow-lg min-w-[200px] py-1" data-testid={`comms-row-menu-${row.id}-open`}>
          {hasLinkedScope && (
            <MenuItem icon={History} label="View timeline" testId="action-view-timeline" onClick={stop(() => onViewTimeline?.(row))} />
          )}
          {!isSms && row.subject && (
            <MenuItem icon={Copy} label="Copy subject" testId="action-copy-subject" onClick={stop(() => copyToClipboard(row.subject, "Subject"))} />
          )}
          <MenuItem icon={Copy} label={isSms ? "Copy message" : "Copy body"} testId="action-copy-body" onClick={stop(() => copyToClipboard(row.message_preview || row.body_text || row.subject || "", isSms ? "Message" : "Body"))} />
          {!isSensitive && (
            <MenuItem icon={RotateCcw} label="Duplicate as new send" testId="action-duplicate" onClick={stop(() => onDuplicate?.(row))} />
          )}
          {isArchived ? (
            <MenuItem icon={ArchiveRestore} label="Restore" testId="action-unarchive" onClick={stop(() => onUnarchive?.(row))} />
          ) : (
            <MenuItem icon={Archive} label="Archive" testId="action-archive" onClick={stop(() => onArchive?.(row))} danger />
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, testId, danger }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`flex items-center gap-2 w-full px-3 py-2 text-[11px] hover:bg-[#FAF8F0] text-left ${danger ? "text-[#B85C5C]" : "text-[#282828]"}`}>
      <Icon className="w-3 h-3" /> {label}
    </button>
  );
}

export function CommunicationsList() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  // Section 17 — timeline drill-in from a comm row.
  const [timelineFor, setTimelineFor] = useState(null);
  const [compose, setCompose] = useState(null);   // { channel, seed? }
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ q, category, limit: 100 });
      if (showArchived) params.set("archived", "1");
      const res = await axios.get(`${API}/admin/communications?${params}`, { headers: auth() });
      setRows(res.data?.communications || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load communications");
    }
    setLoading(false);
  }, [q, category, showArchived]);

  useEffect(() => {
    const t = setTimeout(fetchData, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchData, q]);

  // Clear the bulk selection whenever the visible list changes shape — different
  // filter, different page, archived-toggle flip. Keeping stale IDs around lets
  // an admin accidentally bulk-archive rows they can't see.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [category, q, showArchived]);

  const visibleIds = useMemo(() => rows.map(r => r.id), [rows]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some(id => selectedIds.has(id));

  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      if (allVisibleSelected) {
        // Deselect everything currently visible (keep any off-screen selections — there are none today, but future-proof).
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleArchive = async (row) => {
    try {
      await axios.post(`${API}/admin/communications/${row.id}/archive`, {}, { headers: auth() });
      toast.success("Archived");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Archive failed"); }
  };

  const handleUnarchive = async (row) => {
    try {
      await axios.post(`${API}/admin/communications/${row.id}/unarchive`, {}, { headers: auth() });
      toast.success("Restored");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };

  const handleBulkArchive = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Archive ${ids.length} communication${ids.length === 1 ? "" : "s"}?\n\nArchived rows are hidden from the default feed but kept in the database for audit. You can restore them anytime via "Show archived".`)) return;
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API}/admin/communications/bulk-archive`, { log_ids: ids }, { headers: auth() });
      const { archived = 0, skipped = 0 } = res.data || {};
      toast.success(`Archived ${archived}${skipped ? ` · skipped ${skipped}` : ""}`);
      clearSelection();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk archive failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkUnarchive = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API}/admin/communications/bulk-unarchive`, { log_ids: ids }, { headers: auth() });
      const { restored = 0, skipped = 0 } = res.data || {};
      toast.success(`Restored ${restored}${skipped ? ` · skipped ${skipped}` : ""}`);
      clearSelection();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk restore failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleDuplicate = async (row) => {
    try {
      const res = await axios.get(`${API}/admin/communications/${row.id}/duplicate-seed`, { headers: auth() });
      const seed = res.data?.seed;
      if (!seed) { toast.error("Could not load row for duplication"); return; }
      setCompose({ channel: seed.channel, seed });
    } catch (e) { toast.error(e.response?.data?.detail || "Duplicate failed"); }
  };

  return (
    <div data-testid="communications-list">
      {/* Top toolbar — search, compose buttons, archive toggle */}
      <div className="flex gap-3 mb-4 items-center flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by subject, recipient, booking ID, customer name…"
            className="pl-10 text-[13px] border-[#282828]/10"
            data-testid="comms-search"
          />
        </div>
        <button
          onClick={() => setCompose({ channel: "email" })}
          className="flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-wider bg-[#9A7B4F] text-white hover:bg-[#8B6B3F]"
          data-testid="comms-new-email-btn"
        >
          <Plus className="w-3.5 h-3.5" /><Mail className="w-3.5 h-3.5" /> New Email
        </button>
        <button
          onClick={() => setCompose({ channel: "sms" })}
          className="flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-wider border border-[#9A7B4F]/40 text-[#9A7B4F] bg-white hover:bg-[#FAF8F0]"
          data-testid="comms-new-sms-btn"
        >
          <Plus className="w-3.5 h-3.5" /><Smartphone className="w-3.5 h-3.5" /> New SMS
        </button>
        <button onClick={fetchData} className="p-2 border border-[#282828]/12 hover:border-[#9A7B4F]/40 bg-white" data-testid="comms-refresh" title="Refresh">
          <RefreshCw className={`w-4 h-4 text-[#676664] ${loading ? "animate-spin" : ""}`} />
        </button>
        <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#676664]/70 cursor-pointer select-none ml-1" data-testid="archive-toggle-wrap">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="accent-[#9A7B4F]"
            data-testid="archive-toggle"
          />
          Show archived
        </label>
        <div className="text-[11px] text-[#676664]/50 whitespace-nowrap">{total} total</div>
      </div>

      {/* Category chips */}
      <div className="flex gap-1.5 mb-5 flex-wrap" data-testid="comms-filters">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setCategory(c.key)}
            className={`px-3 py-1 text-[10px] uppercase tracking-wider border transition-colors ${
              category === c.key
                ? "bg-[#282828] text-white border-[#282828]"
                : "border-[#282828]/10 text-[#676664]/70 hover:border-[#9A7B4F]/40 hover:text-[#282828]"
            }`}
            data-testid={`comms-filter-${c.key}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-[#282828]/8 p-10 text-center text-[#676664]/50 text-sm" data-testid="comms-empty">
          {q ? "No communications match your search." : showArchived ? "No archived communications." : "No communications in this view yet."}
        </div>
      ) : (
        <div className="bg-white border border-[#282828]/8" data-testid="comms-table">
          {/* Desktop table (md+) */}
          <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-[11.5px]">
            <thead className="bg-[#FAF8F0] text-[#676664]/60 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2.5 w-8">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    // indeterminate state for partial selection
                    ref={el => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                    onChange={toggleAllVisible}
                    className="accent-[#9A7B4F] cursor-pointer"
                    data-testid="comms-select-all"
                    title={allVisibleSelected ? "Deselect all on this page" : "Select all on this page"}
                  />
                </th>
                <th className="text-left px-3 py-2.5 font-medium">Type</th>
                <th className="text-left px-3 py-2.5 font-medium">Subject / Preview</th>
                <th className="text-left px-3 py-2.5 font-medium">Recipient</th>
                <th className="text-left px-3 py-2.5 font-medium">Dir.</th>
                <th className="text-left px-3 py-2.5 font-medium">Provider</th>
                <th className="text-left px-3 py-2.5 font-medium">Status</th>
                <th className="text-left px-3 py-2.5 font-medium">When</th>
                <th className="text-left px-3 py-2.5 font-medium">Related</th>
                <th className="text-right px-3 py-2.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#282828]/5">
              {rows.map((r) => {
                const st = STATUS_STYLE[r.status] || STATUS_STYLE.sent;
                const isSms = !!r.to_phone || !!r.from_phone;
                const isInbound = r.direction === "inbound";
                const isSelected = selectedIds.has(r.id);
                return (
                  <tr
                    key={r.id}
                    className={`hover:bg-[#FAF8F0]/40 cursor-pointer ${isInbound ? "bg-sky-50/30" : ""} ${r.archived ? "opacity-60" : ""} ${isSelected ? "bg-[#FAF8F0]" : ""}`}
                    onClick={() => setSelectedId(r.id)}
                    data-testid={`comms-row-${r.id}`}
                  >
                    <td className="px-3 py-2.5" onClick={(e) => { e.stopPropagation(); toggleRow(r.id); }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRow(r.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="accent-[#9A7B4F] cursor-pointer"
                        data-testid={`comms-row-select-${r.id}`}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        {isSms ? <Smartphone className="w-3 h-3 text-[#9A7B4F]" /> : <Mail className="w-3 h-3 text-[#9A7B4F]" />}
                        <span className="font-mono text-[10.5px] text-[#282828]">{r.email_type}</span>
                      </div>
                      {isInbound && (
                        <div className="text-[9px] text-sky-700 mt-0.5 font-medium uppercase tracking-wider">Customer reply</div>
                      )}
                      {r.is_sensitive && (
                        <div className="text-[9px] text-amber-600 mt-0.5 italic">sensitive · redacted</div>
                      )}
                      {r.archived && (
                        <div className="text-[9px] text-[#676664]/50 mt-0.5 italic">archived</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 max-w-xs truncate" title={r.subject || r.message_preview || r.body_text}>
                      <div className="text-[#282828]">{r.subject || r.message_preview || r.body_text || <span className="italic text-[#676664]/40">—</span>}</div>
                    </td>
                    <td className="px-3 py-2.5 text-[10.5px] text-[#676664]/80">
                      <div>{isInbound ? (r.from_phone || "—") : (r.to_email || r.to_phone || "—")}</div>
                      {r.recipient_name && <div className="text-[9px] text-[#676664]/50">{r.recipient_name}</div>}
                    </td>
                    <td className="px-3 py-2.5 text-[10px] uppercase text-[#676664]/60">{r.direction}</td>
                    <td className="px-3 py-2.5 text-[10px] uppercase text-[#676664]/60">{r.provider}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[9px] px-1.5 py-0.5 border uppercase tracking-wider inline-flex items-center gap-1 ${st.bg} ${st.text} ${st.border}`}>
                        <st.Icon className="w-2.5 h-2.5" />
                        {r.status}
                      </span>
                      {r.retry_count > 0 && (
                        <div className="text-[9px] text-[#676664]/50 mt-0.5">retry: {r.retry_count}</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-[10px] text-[#676664]/60 whitespace-nowrap">{formatTime(r.sent_at || r.received_at)}</td>
                    <td className="px-3 py-2.5 text-[10px] font-mono text-[#676664]/60 max-w-[14ch] truncate" title={r.booking_id}>
                      {r.booking_id || r.trial_lead_id || "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {isInbound && r.from_phone && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setReplyTo({ to_phone: r.from_phone, customer_name: r.recipient_name, booking_id: r.booking_id, trial_lead_id: r.trial_lead_id, customer_id: r.customer_id }); }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#282828]/12 text-[#282828] bg-white hover:bg-[#FAF8F0]"
                            data-testid={`comms-reply-${r.id}`}
                            title="Reply via SMS"
                          >
                            <Reply className="w-3 h-3" /> Reply
                          </button>
                        )}
                        <RowActionsMenu row={r} onArchive={handleArchive} onUnarchive={handleUnarchive} onDuplicate={handleDuplicate} onViewTimeline={(row) => setTimelineFor(row)} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>{/* /desktop table */}

          {/* Mobile card list (<md) */}
          <ul className="md:hidden divide-y divide-[#282828]/5" data-testid="comms-mobile-list">
            {rows.map((r) => {
              const st = STATUS_STYLE[r.status] || STATUS_STYLE.sent;
              const isSms = !!r.to_phone || !!r.from_phone;
              const isInbound = r.direction === "inbound";
              const isSelected = selectedIds.has(r.id);
              return (
                <li
                  key={r.id}
                  className={`px-3 py-3 ${isInbound ? "bg-sky-50/40" : ""} ${r.archived ? "opacity-60" : ""} ${isSelected ? "bg-[#FAF8F0]" : ""}`}
                  data-testid={`comms-mobile-row-${r.id}`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleRow(r.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 accent-[#9A7B4F] cursor-pointer w-4 h-4"
                      data-testid={`comms-mobile-select-${r.id}`}
                    />
                    <button
                      type="button"
                      onClick={() => setSelectedId(r.id)}
                      className="flex-1 min-w-0 text-left"
                      data-testid={`comms-mobile-open-${r.id}`}
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        {isSms ? <Smartphone className="w-3.5 h-3.5 text-[#9A7B4F]" /> : <Mail className="w-3.5 h-3.5 text-[#9A7B4F]" />}
                        <span className={`text-[9px] px-1.5 py-0.5 border uppercase tracking-wider inline-flex items-center gap-1 ${st.bg} ${st.text} ${st.border}`}>
                          <st.Icon className="w-2.5 h-2.5" />
                          {r.status}
                        </span>
                        {isInbound && <span className="text-[9px] text-sky-700 font-medium uppercase tracking-wider">Reply</span>}
                      </div>
                      <div className="text-[13px] text-[#282828] font-medium truncate">
                        {r.subject || r.message_preview || r.body_text || <span className="italic text-[#676664]/40">(no subject)</span>}
                      </div>
                      <div className="text-[11px] text-[#676664]/70 truncate mt-0.5">
                        {isInbound ? (r.from_phone || "—") : (r.to_email || r.to_phone || "—")}
                        {r.recipient_name ? ` · ${r.recipient_name}` : ""}
                      </div>
                      <div className="flex items-center justify-between mt-1 text-[10px] text-[#676664]/60">
                        <span>{formatTime(r.sent_at || r.received_at)}</span>
                        <span className="font-mono truncate ml-2">{r.booking_id || r.trial_lead_id || ""}</span>
                      </div>
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      {isInbound && r.from_phone && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setReplyTo({ to_phone: r.from_phone, customer_name: r.recipient_name, booking_id: r.booking_id, trial_lead_id: r.trial_lead_id, customer_id: r.customer_id }); }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#282828]/12 text-[#282828] bg-white"
                          data-testid={`comms-mobile-reply-${r.id}`}
                        >
                          <Reply className="w-3 h-3" />
                        </button>
                      )}
                      <RowActionsMenu row={r} onArchive={handleArchive} onUnarchive={handleUnarchive} onDuplicate={handleDuplicate} onViewTimeline={(row) => setTimelineFor(row)} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {selectedId && (
        <CommunicationDetailModal
          logId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => { fetchData(); setSelectedId(null); }}
        />
      )}

      {replyTo && (
        <SmsReplyModal
          to={replyTo}
          onClose={() => setReplyTo(null)}
          onSent={() => { fetchData(); setReplyTo(null); }}
        />
      )}

      {compose && (
        <ComposeMessageModal
          channel={compose.channel}
          seed={compose.seed || null}
          onClose={() => setCompose(null)}
          onSent={() => { fetchData(); setCompose(null); }}
        />
      )}

      {timelineFor && (
        <TimelineDrawer row={timelineFor} onClose={() => setTimelineFor(null)} />
      )}

      {/* Sticky bulk-action footer — appears whenever 1+ rows are selected.
          Mirrors the patterns admins are used to in Gmail / Outlook. */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-64 z-40 bg-[#3D2B1F] text-white border-t border-[#9A7B4F]/40 shadow-2xl" data-testid="bulk-action-footer">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="text-[12px] font-medium" data-testid="bulk-selected-count">
                {selectedIds.size} selected
              </span>
              <button
                onClick={clearSelection}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#C4A77D] hover:text-white"
                data-testid="bulk-clear-selection"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            </div>
            <div className="flex items-center gap-2">
              {showArchived ? (
                <button
                  onClick={handleBulkUnarchive}
                  disabled={bulkBusy}
                  className="inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-wider bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white disabled:opacity-50"
                  data-testid="bulk-unarchive-btn"
                >
                  {bulkBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ArchiveRestore className="w-3.5 h-3.5" />}
                  Restore selected
                </button>
              ) : (
                <button
                  onClick={handleBulkArchive}
                  disabled={bulkBusy}
                  className="inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-wider bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white disabled:opacity-50"
                  data-testid="bulk-archive-btn"
                >
                  {bulkBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
                  Archive selected
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Reserve scroll space so the sticky footer never covers the last row */}
      {selectedIds.size > 0 && <div aria-hidden style={{ height: 64 }} />}
    </div>
  );
}

// ─── Section 17 — Timeline drill-in from a communication row ─────────────────
//
// Opens a side drawer scoped to whichever linked ID the comm row carries.
// Preference order: appointment > trial_lead > booking > customer. The
// Timeline component itself accepts multiple scope props (endpoint ORs them),
// so a comm linked to both an appointment AND a customer still surfaces
// everything the operator cares about — the appointment feed will already
// include cross-scope entries via the shared customer_id.
function TimelineDrawer({ row, onClose }) {
  if (!row) return null;
  const scope = {
    appointmentId: row.appointment_id || "",
    trialLeadId:   row.trial_lead_id || "",
    bookingId:     row.booking_id && /^MHR[-_]/i.test(row.booking_id) ? row.booking_id : "",
    customerId:    row.customer_id || "",
  };
  const primary = scope.appointmentId
    ? "appointment"
    : scope.trialLeadId
    ? "trial"
    : scope.bookingId
    ? "booking"
    : scope.customerId
    ? "customer"
    : null;
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex justify-end"
      onClick={onClose}
      data-testid="timeline-drawer"
    >
      <div
        className="bg-white h-full w-full max-w-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-[#282828]/10 flex items-center justify-between">
          <div>
            <h3 className="text-[13px] uppercase tracking-[0.14em] text-[#3D2B1F]">Timeline</h3>
            <p className="text-[10px] text-[#676664]/60 mt-0.5">
              Scoped by {primary || "no linked entity"}
              {row.recipient_name && ` · ${row.recipient_name}`}
            </p>
          </div>
          <button onClick={onClose} className="text-[#676664]" data-testid="timeline-drawer-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">
          {primary ? (
            <Timeline
              appointmentId={scope.appointmentId}
              trialLeadId={scope.trialLeadId}
              bookingId={scope.bookingId}
              customerId={scope.customerId}
              title=""
            />
          ) : (
            <div className="text-[12px] text-[#676664]/60 p-6 text-center">
              This communication has no linked customer, booking, appointment or trial lead.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

